import type { GraphQLAuditItem } from '@internal-audit/domain/graphql-types';
import { Text } from '@soteria/design-system-ve';

import { LikeDislikeButtons } from '../../LikeDislikeButtons/LikeDislikeButtons';
import * as styles from '../AuditDecisionPanel.css';
import * as spacingStyles from '../spacing.css';

import { NonTriggeredPoliciesSection } from './NonTriggeredPoliciesSection';
import { TriggeredPoliciesSection } from './TriggeredPoliciesSection';

interface PolicyAssessment {
  policyId: string;
  policyName: string;
  isCorrect: boolean | null;
  comments?: string;
}

interface PolicyAssessmentSectionProps {
  selectedItem: GraphQLAuditItem;
  triggeredPolicyAssessments: PolicyAssessment[];
  nonTriggeredPolicyAssessments: PolicyAssessment[];
  isSessionCompleted: boolean;
  policiesCorrect: boolean | null;
  onTriggeredPolicyAssessmentChange: (assessment: PolicyAssessment) => void;
  onNonTriggeredPolicyAssessmentChange: (assessment: PolicyAssessment) => void;
  onPoliciesCorrectChange: (value: boolean) => void;
  className?: string;
}

export function PolicyAssessmentSection({
  selectedItem,
  triggeredPolicyAssessments,
  nonTriggeredPolicyAssessments,
  isSessionCompleted,
  policiesCorrect,
  onTriggeredPolicyAssessmentChange,
  onNonTriggeredPolicyAssessmentChange,
  onPoliciesCorrectChange,
  className,
}: PolicyAssessmentSectionProps) {
  const triggeredPolicies = selectedItem?.contentSnapshot?.triggeredPolicies || [];
  const nonTriggeredPolicies = selectedItem?.contentSnapshot?.nonTriggeredPolicies || [];

  if (triggeredPolicies.length === 0 && nonTriggeredPolicies.length === 0) {
    return null;
  }

  return (
    <div className={className || spacingStyles.sectionContainer}>
      <Text className={styles.sectionTitle}>Policy Assessment</Text>
      <div className={spacingStyles.flexRowSpaceBetween}>
        <Text className={styles.assessmentQuestion}>Do you agree with these policy results?</Text>
        <LikeDislikeButtons disabled={isSessionCompleted} name="policies-assessment" value={policiesCorrect} onChange={onPoliciesCorrectChange} />
      </div>

      <div className={spacingStyles.flexColumnGap}>
        <TriggeredPoliciesSection
          isSessionCompleted={isSessionCompleted}
          selectedItem={selectedItem}
          triggeredPolicyAssessments={triggeredPolicyAssessments}
          onTriggeredPolicyAssessmentChange={onTriggeredPolicyAssessmentChange}
        />

        <NonTriggeredPoliciesSection
          isSessionCompleted={isSessionCompleted}
          nonTriggeredPolicyAssessments={nonTriggeredPolicyAssessments}
          selectedItem={selectedItem}
          onNonTriggeredPolicyAssessmentChange={onNonTriggeredPolicyAssessmentChange}
        />
      </div>
    </div>
  );
}
